import { imageAmountValidation, productPriceValidation, requiredString } from '@/utils/validationRules';
import { validationMessages } from '@/views/gallery/validation-messages';
import type { ArcualItemUnit, ArcualMediaItem } from '@art-basel/artbasel-lib-platform';
import { ArcualItemUnit as ArcualItemUnitValue } from '@art-basel/artbasel-lib-platform';
import type { NumberSchema, StringSchema } from 'yup';
import * as yup from 'yup';
import { EditionSelectorType } from '@/components/Artworks/EditionsSelector/EditionSelectorType';
import { inventoryItemEditionsMessages } from '@/components/Artworks/EditionsSelector/InventoryItemEditionsMessages';

export interface ArtworkValidationValues {
  artistId?: string;
  artistName?: string;
  title?: string;
  year?: string;
  images?: Array<Pick<ArcualMediaItem, 'url'>>;
  retailPrice?: number | null;
  medium?: string;
  unit?: ArcualItemUnit;
  width?: string | number;
  height?: string | number;
  depth?: string | number;
  hours?: string | number;
  minutes?: string | number;
  seconds?: string | number;
  cubicMeters?: string | number;
  cubicFeet?: string | number;
  other?: string;
  editionType?: EditionSelectorType;
}

interface ArtworkValidationOptions {
  editMode?: boolean;
  hasEditionsVolume?: boolean | (() => boolean);
  validateArtistId?: boolean;
  validateArtistName?: boolean;
  validateEditionType?: boolean;
}

const widthAndHeightWhenOptions = {
  is: (value: ArcualItemUnit) =>
    [ArcualItemUnitValue.CM, ArcualItemUnitValue.INCH, ArcualItemUnitValue.PX].includes(value),
  then: (schema: NumberSchema) => schema.required(validationMessages.required),
  otherwise: (schema: NumberSchema) => schema.optional(),
};

const durationWhenOptions = {
  is: (value: ArcualItemUnit) => value === ArcualItemUnitValue.DURATION,
  then: (schema: NumberSchema) => schema.required(validationMessages.required),
  otherwise: (schema: NumberSchema) => schema.optional(),
};

const volumeCubicMetersWhenOptions = {
  is: (value: ArcualItemUnit) => value === ArcualItemUnitValue.VOLUME_CUBIC_METERS,
  then: (schema: NumberSchema) => schema.required(validationMessages.required),
  otherwise: (schema: NumberSchema) => schema.optional(),
};

const volumeCubicFeetWhenOptions = {
  is: (value: ArcualItemUnit) => value === ArcualItemUnitValue.VOLUME_CUBIC_FEET,
  then: (schema: NumberSchema) => schema.required(validationMessages.required),
  otherwise: (schema: NumberSchema) => schema.optional(),
};

const otherWhenOptions = {
  is: (value: ArcualItemUnit) => value === ArcualItemUnitValue.OTHER,
  then: (schema: StringSchema) => schema.required(validationMessages.required),
  otherwise: (schema: StringSchema) => schema.optional(),
};

const handleEmptyString = (value: number, originalValue: string | number | null | undefined) => {
  if (typeof originalValue === 'string' && originalValue.trim() === '') {
    return undefined;
  }

  return value;
};

export const createArtworkValidationSchema = (options: ArtworkValidationOptions = {}) => {
  const {
    editMode = false,
    hasEditionsVolume = false,
    validateArtistId = false,
    validateArtistName = false,
    validateEditionType = false,
  } = options;
  const getHasEditionsVolume = () =>
    typeof hasEditionsVolume === 'function' ? hasEditionsVolume() : hasEditionsVolume;

  return yup.object({
    artistName: validateArtistName ? requiredString : yup.string().optional(),
    artistId: validateArtistId ? requiredString : yup.string().optional(),
    title: requiredString,
    year: requiredString,
    images: imageAmountValidation,
    retailPrice: productPriceValidation,
    medium: requiredString,
    unit: requiredString,
    width: yup
      .number()
      .typeError(validationMessages.number)
      .transform(handleEmptyString)
      .when('unit', widthAndHeightWhenOptions),
    height: yup
      .number()
      .typeError(validationMessages.number)
      .transform(handleEmptyString)
      .when('unit', widthAndHeightWhenOptions),
    depth: yup.number().typeError(validationMessages.number).transform(handleEmptyString).optional(),
    hours: yup
      .number()
      .typeError(validationMessages.number)
      .transform(handleEmptyString)
      .when('unit', durationWhenOptions),
    minutes: yup
      .number()
      .typeError(validationMessages.number)
      .transform(handleEmptyString)
      .when('unit', durationWhenOptions),
    seconds: yup
      .number()
      .typeError(validationMessages.number)
      .transform(handleEmptyString)
      .when('unit', durationWhenOptions),
    cubicMeters: yup
      .number()
      .typeError(validationMessages.number)
      .transform(handleEmptyString)
      .when('unit', volumeCubicMetersWhenOptions),
    cubicFeet: yup
      .number()
      .typeError(validationMessages.number)
      .transform(handleEmptyString)
      .when('unit', volumeCubicFeetWhenOptions),
    other: yup.string().when('unit', otherWhenOptions),
    editionType: validateEditionType
      ? requiredString.test({
          test: (value) => editMode || value === EditionSelectorType.UNIQUE || getHasEditionsVolume(),
          message: inventoryItemEditionsMessages.bothEmpty,
        })
      : yup.string().optional(),
  });
};
